/** School-driven chat guide identity (avatar + copy). Default is Parashari AskGuru. */

export type AskGuideIdentity = {
  faceUrl: string;
  /** CSS object-position for circular crop (bust portraits need top). */
  faceObjectPosition: 'center' | 'top';
  shortName: string;
  displayNameEn: string;
  displayNameHi: string;
  thinkingEn: string;
  thinkingHi: string;
  ariaAskEn: string;
  ariaAskHi: string;
  scopeHintEn: string;
  scopeHintHi: string;
  footerPageEn: string;
  footerPageHi: string;
  footerGlobalEn: string;
  footerGlobalHi: string;
  emptyTaglineEn: string;
  emptyTaglineHi: string;
  dockedHintEn: string;
  dockedHintHi: string;
  placeholderEn: string;
  placeholderHi: string;
  /** When true, show a school-only scope banner and lock prompt framing. */
  scopedSchoolOnly: boolean;
};

const BHRIGU_FACE = '/schools/parashari/assets/mah-parashar.png';
const JAIMINI_FACE = '/schools/jaimini/jaimini.png';
const PROF_KP_FACE = '/schools/kp/assets/prof-kp.png';
const MAH_BHRIGU_BNN_FACE = '/schools/bnn/assets/mah-bhrigu.png';
const ROOP_CHAND_FACE = '/schools/lal-kitab/assets/roop-chand-joshi.png';
const SHIVA_PARVATI_FACE = '/schools/swara-vigyan/assets/shiva-parvati.png';
const SURYA_SIDDHANTA_FACE = '/schools/surya-siddhanta/assets/SuryaSid.png';

const PARASHAR_GUIDE: AskGuideIdentity = {
  faceUrl: BHRIGU_FACE,
  faceObjectPosition: 'top',
  shortName: 'AskGuru',
  displayNameEn: 'Mah. Parashar',
  displayNameHi: 'महा. पराशर',
  thinkingEn: 'Mah. Parashar is thinking…',
  thinkingHi: 'महा. पराशर सोच रहे हैं…',
  ariaAskEn: 'Ask Mah. Parashar about this page',
  ariaAskHi: 'इस पृष्ठ पर महा. पराशर से पूछें',
  scopeHintEn: 'Grounded in Takshify Jyotisha syllabus · learning only.',
  scopeHintHi: 'Takshify ज्योतिष पाठ्यक्रम · केवल सीखने के लिए।',
  footerPageEn: 'Ask Mah. Parashar about this lesson · free on this page · learning only.',
  footerPageHi: 'इस पाठ पर महा. पराशर से पूछें · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Chat with Mah. Parashar · grounded in Takshify syllabus · learning only.',
  footerGlobalHi: 'महा. पराशर से बातचीत · Takshify पाठ्यक्रम · केवल सीखने के लिए।',
  emptyTaglineEn: 'Stars, charts, nakshatras - ask Mah. Parashar anything.',
  emptyTaglineHi: 'तारों, कुंडली, नक्षत्र - महा. पराशर से कुछ भी पूछें।',
  dockedHintEn: 'Chat beside Mah. Parashar',
  dockedHintHi: 'महा. पराशर के साथ बातचीत',
  placeholderEn: 'Ask Mah. Parashar…',
  placeholderHi: 'महा. पराशर से पूछें…',
  scopedSchoolOnly: false,
};

const JAIMINI_GUIDE: AskGuideIdentity = {
  faceUrl: JAIMINI_FACE,
  faceObjectPosition: 'center',
  shortName: 'Mah. Jaimini',
  displayNameEn: 'Maharishi Jaimini',
  displayNameHi: 'महर्षि जैमिनी',
  thinkingEn: 'Maharishi Jaimini is thinking…',
  thinkingHi: 'महर्षि जैमिनी सोच रहे हैं…',
  ariaAskEn: 'Ask Maharishi Jaimini about Jaimini astrology on this page',
  ariaAskHi: 'इस पृष्ठ पर जैमिनी ज्योतिष के बारे में महर्षि जैमिनी से पूछें',
  scopeHintEn:
    'Ask only about Jaimini astrology. Other traditions are out of scope here.',
  scopeHintHi:
    'केवल जैमिनी ज्योतिष पूछें। अन्य परंपराएँ यहाँ विषय से बाहर हैं।',
  footerPageEn: 'Ask Maharishi Jaimini · Jaimini astrology only · free on this page · learning only.',
  footerPageHi: 'महर्षि जैमिनी से पूछें · केवल जैमिनी ज्योतिष · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Ask Maharishi Jaimini · Jaimini astrology only · learning only.',
  footerGlobalHi: 'महर्षि जैमिनी से पूछें · केवल जैमिनी ज्योतिष · केवल सीखने के लिए।',
  emptyTaglineEn:
    'Chara Karakas, Rashi Drishti, Arudha Padas - ask Maharishi Jaimini about Jaimini astrology only.',
  emptyTaglineHi:
    'चर कारक, राशि दृष्टि, आरूढ़ पद - महर्षि जैमिनी से केवल जैमिनी ज्योतिष पूछें।',
  dockedHintEn: 'Chat with Maharishi Jaimini · Jaimini only',
  dockedHintHi: 'महर्षि जैमिनी के साथ बातचीत · केवल जैमिनी',
  placeholderEn: 'Ask Maharishi Jaimini about Jaimini astrology…',
  placeholderHi: 'महर्षि जैमिनी से जैमिनी ज्योतिष पूछें…',
  scopedSchoolOnly: true,
};

const PROF_KP_GUIDE: AskGuideIdentity = {
  faceUrl: PROF_KP_FACE,
  faceObjectPosition: 'top',
  shortName: 'Prof KP',
  displayNameEn: 'Prof KP',
  displayNameHi: 'प्रो. KP',
  thinkingEn: 'Prof KP is thinking…',
  thinkingHi: 'प्रो. KP सोच रहे हैं…',
  ariaAskEn: 'Ask Prof KP about KP astrology on this page',
  ariaAskHi: 'इस पृष्ठ पर KP ज्योतिष के बारे में प्रो. KP से पूछें',
  scopeHintEn:
    'Ask only about KP (Krishnamurti Paddhati) astrology. Other traditions are out of scope here.',
  scopeHintHi:
    'केवल KP (कृष्णमूर्ति पद्धति) ज्योतिष पूछें। अन्य परंपराएँ यहाँ विषय से बाहर हैं।',
  footerPageEn: 'Ask Prof KP · KP astrology only · free on this page · learning only.',
  footerPageHi: 'प्रो. KP से पूछें · केवल KP ज्योतिष · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Ask Prof KP · KP (Krishnamurti Paddhati) astrology only · learning only.',
  footerGlobalHi: 'प्रो. KP से पूछें · केवल KP (कृष्णमूर्ति पद्धति) ज्योतिष · केवल सीखने के लिए।',
  emptyTaglineEn: 'Cusps, sub-lords, significators - ask Prof KP about KP astrology only.',
  emptyTaglineHi: 'कस्प, सब-लॉर्ड, सिग्निफिकेटर - प्रो. KP से केवल KP ज्योतिष पूछें।',
  dockedHintEn: 'Chat with Prof KP · KP only',
  dockedHintHi: 'प्रो. KP के साथ बातचीत · केवल KP',
  placeholderEn: 'Ask Prof KP about KP astrology…',
  placeholderHi: 'प्रो. KP से KP ज्योतिष पूछें…',
  scopedSchoolOnly: true,
};

const BNN_BHRIGU_GUIDE: AskGuideIdentity = {
  faceUrl: MAH_BHRIGU_BNN_FACE,
  faceObjectPosition: 'top',
  shortName: 'Mah. Bhrigu',
  displayNameEn: 'Mah. Bhrigu',
  displayNameHi: 'महा. भृगु',
  thinkingEn: 'Mah. Bhrigu is thinking…',
  thinkingHi: 'महा. भृगु सोच रहे हैं…',
  ariaAskEn: 'Ask Mah. Bhrigu about Bhrigu Nandi Nadi on this page',
  ariaAskHi: 'इस पृष्ठ पर भृगु नंदी नाड़ी के बारे में महा. भृगु से पूछें',
  scopeHintEn:
    'Ask only about Bhrigu Nandi Nadi (BNN) astrology. Other traditions are out of scope here.',
  scopeHintHi:
    'केवल भृगु नंदी नाड़ी (BNN) ज्योतिष पूछें। अन्य परंपराएँ यहाँ विषय से बाहर हैं।',
  footerPageEn: 'Ask Mah. Bhrigu · BNN only · free on this page · learning only.',
  footerPageHi: 'महा. भृगु से पूछें · केवल BNN · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Ask Mah. Bhrigu · Bhrigu Nandi Nadi (BNN) only · learning only.',
  footerGlobalHi: 'महा. भृगु से पूछें · केवल भृगु नंदी नाड़ी (BNN) · केवल सीखने के लिए।',
  emptyTaglineEn: 'Transit, karma, destinies - ask Mah. Bhrigu about BNN only.',
  emptyTaglineHi: 'गोचर, कर्म, भाग्य - महा. भृगु से केवल BNN पूछें।',
  dockedHintEn: 'Chat with Mah. Bhrigu · BNN only',
  dockedHintHi: 'महा. भृगु के साथ बातचीत · केवल BNN',
  placeholderEn: 'Ask Mah. Bhrigu about Bhrigu Nandi Nadi…',
  placeholderHi: 'महा. भृगु से भृगु नंदी नाड़ी पूछें…',
  scopedSchoolOnly: true,
};

const ROOP_CHAND_GUIDE: AskGuideIdentity = {
  faceUrl: ROOP_CHAND_FACE,
  faceObjectPosition: 'top',
  shortName: 'Pt. Roop Chand',
  displayNameEn: 'Pt. Roop Chand',
  displayNameHi: 'पं. रूप चंद',
  thinkingEn: 'Pt. Roop Chand is thinking…',
  thinkingHi: 'पं. रूप चंद सोच रहे हैं…',
  ariaAskEn: 'Ask Pt. Roop Chand about Lal Kitab on this page',
  ariaAskHi: 'इस पृष्ठ पर लाल किताब के बारे में पं. रूप चंद से पूछें',
  scopeHintEn:
    'Ask only about Lal Kitab astrology. Other traditions are out of scope here.',
  scopeHintHi:
    'केवल लाल किताब ज्योतिष पूछें। अन्य परंपराएँ यहाँ विषय से बाहर हैं।',
  footerPageEn: 'Ask Pt. Roop Chand · Lal Kitab only · free on this page · learning only.',
  footerPageHi: 'पं. रूप चंद से पूछें · केवल लाल किताब · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Ask Pt. Roop Chand · Lal Kitab astrology only · learning only.',
  footerGlobalHi: 'पं. रूप चंद से पूछें · केवल लाल किताब ज्योतिष · केवल सीखने के लिए।',
  emptyTaglineEn:
    'Fixed houses, palmistry, Upayas - ask Pt. Roop Chand about Lal Kitab only.',
  emptyTaglineHi:
    'स्थिर भाव, हस्तरेखा, उपाय - पं. रूप चंद से केवल लाल किताब पूछें।',
  dockedHintEn: 'Chat with Pt. Roop Chand · Lal Kitab only',
  dockedHintHi: 'पं. रूप चंद के साथ बातचीत · केवल लाल किताब',
  placeholderEn: 'Ask Pt. Roop Chand about Lal Kitab…',
  placeholderHi: 'पं. रूप चंद से लाल किताब पूछें…',
  scopedSchoolOnly: true,
};

const MAHADEV_SWARA_GUIDE: AskGuideIdentity = {
  faceUrl: SHIVA_PARVATI_FACE,
  faceObjectPosition: 'center',
  shortName: 'Mahadev',
  displayNameEn: 'Mahadev',
  displayNameHi: 'महादेव',
  thinkingEn: 'Mahadev is thinking…',
  thinkingHi: 'महादेव सोच रहे हैं…',
  ariaAskEn: 'Ask Mahadev about Swara Vigyan on this page',
  ariaAskHi: 'इस पृष्ठ पर स्वर विज्ञान के बारे में महादेव से पूछें',
  scopeHintEn:
    'Ask only about Swara Vigyan (nostril breath science). Other traditions are out of scope here.',
  scopeHintHi:
    'केवल स्वर विज्ञान (नासिका श्वास विज्ञान) पूछें। अन्य परंपराएँ यहाँ विषय से बाहर हैं।',
  footerPageEn: 'Ask Mahadev · Swara Vigyan only · free on this page · learning only.',
  footerPageHi: 'महादेव से पूछें · केवल स्वर विज्ञान · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Ask Mahadev · Swara Vigyan only · learning only.',
  footerGlobalHi: 'महादेव से पूछें · केवल स्वर विज्ञान · केवल सीखने के लिए।',
  emptyTaglineEn:
    'Ida, Pingala, Sushumna - ask Mahadev about nostril timing and daily Swara Vigyan only.',
  emptyTaglineHi:
    'इडा, पिंगला, सुषुम्णा - महादेव से केवल नासिका समय और दैनिक स्वर विज्ञान पूछें।',
  dockedHintEn: 'Chat with Mahadev · Swara Vigyan only',
  dockedHintHi: 'महादेव के साथ बातचीत · केवल स्वर विज्ञान',
  placeholderEn: 'Ask Mahadev about Swara Vigyan…',
  placeholderHi: 'महादेव से स्वर विज्ञान पूछें…',
  scopedSchoolOnly: true,
};

const SURYA_SIDDHANTA_GUIDE: AskGuideIdentity = {
  faceUrl: SURYA_SIDDHANTA_FACE,
  faceObjectPosition: 'center',
  shortName: 'Surya',
  displayNameEn: 'Surya',
  displayNameHi: 'सूर्य',
  thinkingEn: 'Surya is thinking…',
  thinkingHi: 'सूर्य सोच रहे हैं…',
  ariaAskEn: 'Ask Surya about Surya Siddhanta on this page',
  ariaAskHi: 'इस पृष्ठ पर सूर्य सिद्धांत के बारे में सूर्य से पूछें',
  scopeHintEn:
    'Ask only about Surya Siddhanta (Ganita astronomy and calendar math). Other traditions are out of scope here.',
  scopeHintHi:
    'केवल सूर्य सिद्धांत (गणित खगोल और कैलेंडर गणित) पूछें। अन्य परंपराएँ यहाँ विषय से बाहर हैं।',
  footerPageEn: 'Ask Surya · Surya Siddhanta only · free on this page · learning only.',
  footerPageHi: 'सूर्य से पूछें · केवल सूर्य सिद्धांत · यहाँ मुफ़्त · केवल सीखने के लिए।',
  footerGlobalEn: 'Ask Surya · Surya Siddhanta only · learning only.',
  footerGlobalHi: 'सूर्य से पूछें · केवल सूर्य सिद्धांत · केवल सीखने के लिए।',
  emptyTaglineEn:
    'Mahayuga, mean motions, ayanamsha - ask Surya about Surya Siddhanta Ganita only.',
  emptyTaglineHi:
    'महायुग, औसत गति, अयनांश - सूर्य से केवल सूर्य सिद्धांत गणित पूछें।',
  dockedHintEn: 'Chat with Surya · Surya Siddhanta only',
  dockedHintHi: 'सूर्य के साथ बातचीत · केवल सूर्य सिद्धांत',
  placeholderEn: 'Ask Surya about Surya Siddhanta…',
  placeholderHi: 'सूर्य से सूर्य सिद्धांत पूछें…',
  scopedSchoolOnly: true,
};

/**
 * Resolve chat guide branding for an Ask school scope.
 * @param school - active school slug (`kp`, `bnn`, `lal-kitab`, `swara-vigyan`, `surya-siddhanta`, `parashari`, …) or chip id
 */
export function getAskGuideForSchool(school?: string | null): AskGuideIdentity {
  const key = String(school || 'parashari').toLowerCase().trim();
  if (key === 'jaimini') return JAIMINI_GUIDE;
  if (key === 'kp') return PROF_KP_GUIDE;
  if (key === 'bnn') return BNN_BHRIGU_GUIDE;
  if (key === 'lal-kitab') return ROOP_CHAND_GUIDE;
  if (key === 'swara-vigyan') return MAHADEV_SWARA_GUIDE;
  if (key === 'surya-siddhanta') return SURYA_SIDDHANTA_GUIDE;
  return PARASHAR_GUIDE;
}

export {
  BHRIGU_FACE,
  JAIMINI_FACE,
  PROF_KP_FACE,
  MAH_BHRIGU_BNN_FACE,
  ROOP_CHAND_FACE,
  SHIVA_PARVATI_FACE,
  SURYA_SIDDHANTA_FACE,
};
